import { useCallback, useMemo, useState } from "react";

import { useUserData } from "Flocknote/apiHooks";
import { useLoginLevel } from "Flocknote/stateHooks";

import API from "Signups/API";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";
import { useEntityPlural } from "Signups/stateHooks";

import { useNumberOfSlotsFromRespondents } from "./utils/getRespondentNumbers";

const useAddRespondentIsEnabled = (
  respondents,
  { totalSlots, slotsTaken: slotsTakenState, maxSingleUserSlots }
) => {
  const entityPlural = useEntityPlural();
  const currentRespondentSlots = useNumberOfSlotsFromRespondents(respondents);
  const [originalRespondentSlots] = useState(currentRespondentSlots);

  const slotsTaken = useMemo(() => {
    const currentChange = originalRespondentSlots - currentRespondentSlots;
    return slotsTakenState - currentChange;
  }, [currentRespondentSlots, originalRespondentSlots, slotsTakenState]);

  const disabledMessage = useMemo(() => {
    if (currentRespondentSlots >= maxSingleUserSlots)
      return `You've reached the max number of ${entityPlural} per person!`;

    if (slotsTaken >= totalSlots)
      return `This signup item has reached its capacity!`;

    return "";
  }, [
    currentRespondentSlots,
    entityPlural,
    maxSingleUserSlots,
    slotsTaken,
    totalSlots,
  ]);

  const isEnabled = useMemo(() => !disabledMessage, [disabledMessage]);

  return [isEnabled, disabledMessage];
};

const useHandleAuthSuccess = () => {
  const [state, dispatch] = useSignups();
  const { isPollType } = useGetSignupListType();
  const [errorMessage, setErrorMessage] = useState("");

  const signupListID = state.currentSignupList.ID;

  const handleAuthSuccess = useCallback(() => {
    API.getSignupListInfo(signupListID)
      .post()
      .then((resp) => {
        dispatch({
          type: "Set Current Signup List",
          triggerRespondentRefresh: true,
          ...resp,
        });
        if (isPollType) {
          dispatch({
            type: "expandedItemID",
            ID: null,
          });
        }
      })
      .catch((err) => {
        setErrorMessage(
          "Something went wrong. Please refresh and try again. If the problem persists, contact help@flocknote.com"
        );
      });
  }, [dispatch, isPollType, signupListID]);

  return { errorMessage, handleAuthSuccess };
};

const useShowNewUserForm = (hasExtendedFields) => {
  const loginLevel = useLoginLevel();
  const [{ fname }] = useUserData();

  const showNewUserForm = useMemo(
    () => !fname || !loginLevel || (hasExtendedFields && loginLevel !== "full"),
    [fname, hasExtendedFields, loginLevel]
  );

  return showNewUserForm;
};

export { useAddRespondentIsEnabled, useHandleAuthSuccess, useShowNewUserForm };
